import { useMemo } from 'react';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { memberId } from '@trello/session-cookie';
import { useHasButlerAccessQuery } from './HasButlerAccessQuery.generated';

/**
 * Checks whether the current user has access to Butler features.
 * At the time of implementation, every Trello user has access to Butler
 * _except_ for non-admin users in enterprises that have explicitly restricted
 * Butler access to admins only. Today, the only enterprise this logic applies
 * to is Bloomberg; for all other users, this will exit fairly early.
 *
 * Functional equivalent of `canShowButlerUI` on the board model; I didn't want
 * to name it `UI` because GraphQL codegen lowercases the I and it looked weird.
 */
export const useHasButlerAccess = (idBoard: string): boolean => {
  const adminOnlyEnterpriseIds = useFeatureFlag<string[]>(
    'workflowers.butler-ent-admin-only-allowlist',
    [],
  );
  const { data } = useHasButlerAccessQuery({
    variables: { idBoard },
    waitOn: ['CurrentBoardFull'],
  });
  const board = data?.board;
  return useMemo(() => {
    if (!board) {
      return false;
    }
    const { idEnterprise } = board;
    if (!idEnterprise || !adminOnlyEnterpriseIds.includes(idEnterprise)) {
      return true;
    }
    const { organization } = board;
    if (!memberId || !organization) {
      return false;
    }
    const { idAdmins } = organization.enterprise ?? {};
    return (
      idAdmins?.includes(memberId) ||
      organization.memberships?.find(
        (membership) => membership.idMember === memberId,
      )?.memberType === 'admin'
    );
  }, [adminOnlyEnterpriseIds, board]);
};
